import React, { MouseEventHandler, useRef } from 'react';
import { isIOS } from 'react-device-detect';

import { DetailWidgetInteractionItem, StyleProps } from '../WidgetElement';
import { sendInteractGATrackEventData } from '../../../../util/gAUtil';
import { getBorderStyle, getDropShadowStyle, getWidgetOpacity } from '../util';
import { useWidgetAnimation } from '../../../Player/hooks/useWidgetAnimation';

const generateHotspotStyle = (isHover: boolean, clickable: boolean, style: StyleProps) => {
	const {
		transformSettings: {
			borderRadiusBottom,
			borderRadiusLeft,
			borderRadiusRight,
			borderRadiusTop,
			paddingBottom,
			paddingLeft,
			paddingRight,
			paddingTop,
			hoverPaddingBottom,
			hoverPaddingLeft,
			hoverPaddingRight,
			hoverPaddingTop,
		},
		backgroundSettings: {
			backgroundColor,
			hoverBackgroundColor,
		},
	} = style;

	const { border, hoverBorder } = getBorderStyle(style.backgroundSettings);

	return {
		display: 'flex',
		width: '100%',
		height: '100%',
		cursor: clickable ? 'pointer' : 'default',
		backgroundColor: isHover ? (hoverBackgroundColor || backgroundColor) : backgroundColor,
		borderTopRightRadius: `${borderRadiusTop}px`,
		borderBottomRightRadius: `${borderRadiusRight}px`,
		borderBottomLeftRadius: `${borderRadiusBottom}px`,
		borderTopLeftRadius: `${borderRadiusLeft}px`,
		paddingBottom: isHover ? (hoverPaddingBottom ?? paddingBottom) : paddingBottom,
		paddingLeft: isHover ? (hoverPaddingLeft ?? paddingLeft) : paddingLeft,
		paddingRight: isHover ? (hoverPaddingRight ?? paddingRight) : paddingRight,
		paddingTop: isHover ? (hoverPaddingTop ?? paddingTop) : paddingTop,
		border: isHover ? (hoverBorder || border) : border,
	};
};

const generateWrapperStyle = ({
	style,
	isHover,
	clickable,
}: {
	style: StyleProps;
	isHover: boolean;
	clickable?: boolean;
}) => {
	const { dropShadow, hoverDropShadow } = getDropShadowStyle(style.transformSettings);
	return {
		filter: "drop-shadow(" + (isHover ? (hoverDropShadow || dropShadow) : dropShadow) + ")",
	} as React.CSSProperties;
};

const Hotspot = ({
	id,
	style,
	data,
	clickable,
	isGoToSceneClick,
	isHover,
	scenarioId,
	sceneId,
	widgetRef,
	isShowWidget,
	isExiting,
	onExitDone,
	exitTiming,
	onClickExitStart,
	onClick,
	handleMouseEnter,
	handleMouseLeave,
}: DetailWidgetInteractionItem) => {
	const {
		coordinate: { x, y },
		dimension: { w = 150, h = 100 },
		rotation,
	} = style;

	const { name, type } = data;

	const gElementRef = useRef<SVGGElement | null>();
	const bgElementRef = useRef<HTMLDivElement | null>();
	const { triggerClickAnimation } = useWidgetAnimation({ gElementRef, bgElementRef, style, isShowWidget, isExiting, onExitDone, exitTiming, onClickExitStart, clickable });

	const handleWidgetAction = (event: React.TouchEvent | React.MouseEvent | React.KeyboardEvent) => {
		event.stopPropagation();
		event.persist?.();
		sendInteractGATrackEventData(type, name, scenarioId, sceneId);
		triggerClickAnimation(() => onClick(event));
	};

	const handleTouchStart = (event: React.TouchEvent) => {
		event.preventDefault();
		handleWidgetAction(event);
	};

	const handleOnKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === 'Enter' || event.key === ' ') {
			handleWidgetAction(event);
		}
	}

	return (<g style={{ transform: `rotate(${rotation ?? 0}deg)`, opacity: getWidgetOpacity(style.backgroundSettings, isHover) }}>
		<g
			id={id}
			onClick={handleWidgetAction}
			onTouchStart={isIOS ? handleTouchStart : undefined}
			onMouseEnter={handleMouseEnter as MouseEventHandler<SVGGElement>}
			onMouseLeave={handleMouseLeave as MouseEventHandler<SVGGElement>}
			onKeyDown={handleOnKeyDown}
			tabIndex={0}
			role='button'
			aria-label={name}
			style={generateWrapperStyle({ style, isHover, clickable })}
			ref={r => gElementRef.current = r}
		>
			<foreignObject
				ref={widgetRef}
				x={x}
				y={y}
				width={w}
				height={h}
				aria-hidden="true"
			>
				<div ref={r => bgElementRef.current = r} aria-hidden="true" style={generateHotspotStyle(isHover, clickable, style)} />
			</foreignObject>
		</g>
	</g>);
};

export default Hotspot;
